import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { getDefaultJournal, getUI, getUser } from 'redux/selectors';
import * as JournalActions from 'redux/actions/journal';
import './MissionJournalSetupDialog.css';
import { JOURNAL_STATE } from 'common/constant';
import iconLock from 'assets/images/icons_svg/icon_lock.svg';
import iconCaretDown from 'assets/images/icons_svg/icon_caret-down.svg';
import iconCalendar from 'assets/images/icons_svg/icon_mission_calendar.svg';

const MissionJournalSetupDialog = ({
    close,
    dispatch,
    ui,
    user,
    defaultJournal,
    onBack,
    onSave,
    initialData = {
        title: '',
        description: '',
        missionStartDate: '',
        missionStatus: ''
    }
}) => {
    const [journalTitle, setJournalTitle] = useState(initialData?.title || '');
    const [missionStartDate, setMissionStartDate] = useState(
        initialData?.missionStartDate || ''
    );
    const [missionStatus, setMissionStatus] = useState(
        initialData?.missionStatus || ''
    );
    const [isSaving, setIsSaving] = useState(false);

    const TITLE_LIMIT = 50;

    // Helper to get today's date in YYYY-MM-DD
    const todayStr = new Date().toISOString().split('T')[0];

    const _close = useCallback(() => {
        if (close) {
            close();
        }
    }, [close]);

    const _onBack = useCallback(() => {
        if (onBack) {
            onBack();
        }
    }, [onBack]);

    const _onNext = useCallback(() => {
        if (!journalTitle || !missionStatus || !missionStartDate) {
            return;
        }

        setIsSaving(true);
        const payload = {
            title: journalTitle,
            title_page_content: '',
            journal_type: 'Mission',
            journal_pattern: 77,
            is_default: false,
            state: JOURNAL_STATE.PRIVATE_SHOWN,
            mission_start_date: missionStartDate || null,
            mission_end_date: null,
            default_entry_privacy: 'Me',
            missionary_status: missionStatus,
            shared_with: [],
            tags: []
        };

        dispatch(JournalActions.create(payload))
            .then((response) => {
                setIsSaving(false);
                const createdJournal = response?.payload?.data || payload;
                if (onSave) {
                    onSave(createdJournal);
                }
                _close();
            })
            .catch(() => {
                setIsSaving(false);
                _close();
            });
    }, [
        dispatch,
        journalTitle,
        missionStartDate,
        missionStatus,
        onSave,
        _close
    ]);

    return (
        <div
            id="mission-journal-setup-modal"
            className="modal-frame centered-modal-frame"
        >
            <div className="modal-box modal-box--mission-setup">
                <div className="modal__header">Mission JRNL Setup</div>
                <div className="modal__body mission-setup-body">
                    <div className="setup-form-group">
                        <div className="setup-form-label-row">
                            <label>
                                Journal Title
                                <span className="required-star">*</span>
                            </label>
                            <span className="char-count">
                                {journalTitle.length}/{TITLE_LIMIT}
                            </span>
                        </div>
                        <input
                            type="text"
                            placeholder="Your journal title"
                            value={journalTitle}
                            onChange={(e) =>
                                setJournalTitle(
                                    e.target.value.slice(0, TITLE_LIMIT)
                                )
                            }
                            className="setup-input"
                        />
                    </div>

                    <div className="setup-form-row-multi">
                        <div className="setup-form-group field-privacy">
                            <label className="mb-2 block">Privacy</label>
                            <div className="privacy-selector">
                                <img
                                    src={iconLock}
                                    alt="Lock"
                                    className="privacy-icon"
                                />
                                <span>Private</span>
                            </div>
                        </div>

                        <div className="setup-form-group field-status">
                            <label className="mb-2 block">
                                Mission Status{' '}
                                <span className="required-star">*</span>
                            </label>
                            <div className="dropdown-wrapper">
                                <select
                                    className={`setup-input setup-select ${
                                        !missionStatus
                                            ? 'placeholder-active'
                                            : ''
                                    }`}
                                    value={missionStatus}
                                    onChange={(e) =>
                                        setMissionStatus(e.target.value)
                                    }
                                >
                                    <option value="" disabled>
                                        Choose one
                                    </option>
                                    <option value="starting_the_mission">
                                        Starting the mission
                                    </option>
                                    <option value="currently_serving">
                                        Currently serving
                                    </option>
                                    <option value="ended_the_mission">
                                        Ended the mission
                                    </option>
                                </select>
                                <img
                                    src={iconCaretDown}
                                    alt=""
                                    className="select-caret"
                                />
                            </div>
                        </div>

                        <div className="setup-form-group field-date">
                            <label className="mb-2 block">
                                Mission Start Date{' '}
                                <span className="required-star">*</span>
                            </label>
                            <div className="input-with-icon">
                                <img
                                    src={iconCalendar}
                                    alt=""
                                    className="input-icon"
                                />
                                <input
                                    type="date"
                                    value={missionStartDate}
                                    onChange={(e) =>
                                        setMissionStartDate(e.target.value)
                                    }
                                    className="setup-input setup-input--date"
                                    disabled={!missionStatus}
                                    max={
                                        missionStatus === 'currently_serving' ||
                                        missionStatus === 'ended_the_mission'
                                            ? todayStr
                                            : undefined
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal__footer mission-setup-footer">
                    <button className="btn btn-back" onClick={_onBack}>
                        Back
                    </button>
                    <button
                        className={`btn btn-gradient btn-next ${
                            !journalTitle || !missionStatus || !missionStartDate
                                ? 'disabled'
                                : ''
                        }`}
                        onClick={_onNext}
                        disabled={
                            isSaving ||
                            !journalTitle ||
                            !missionStatus ||
                            !missionStartDate
                        }
                    >
                        {isSaving ? 'Creating...' : 'Create Journal'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const state = createStructuredSelector({
    defaultJournal: getDefaultJournal,
    ui: getUI,
    user: getUser
});

export default connect(state)(MissionJournalSetupDialog);
